import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react'
import { Divider } from 'primereact/divider'
import {
    LuxIconSpinner,
    LuxIcon,
    Color,
    LuxDateRangePicker,
    LuxButton,
} from '@lux/wardrobe-lib'
import {
    GlobalSearchInput,
    GlobalSearchOutput,
    SectionFilter,
    SectionQuery,
    SectionType,
} from '@/app/models/search'
import { GlobalSearchList } from './GlobalSearchList'
import {
    AutoComplete,
    AutoCompleteCompleteMethodParams,
} from 'primereact/autocomplete'
import { GlobalSearchService } from '@/app/services/GlobalSearchService'
import { useIntl } from 'react-intl'
import LuxToggleButton from '../UI/ToggleButton/LuxToggleButton'
import { Markup } from 'interweave'
import moment from 'moment'

type GlobalSearchContentProps = {
    globalFiltersList: any[]
    searchInput: GlobalSearchInput
    searchOutput: GlobalSearchOutput
    onResetSearch: () => void
    onSearch: (params: GlobalSearchInput) => void
    onListItemClick: (item: any, key: string) => void
    requesting: boolean
}

export const GlobalSearchContent = forwardRef(
    (props: GlobalSearchContentProps, ref: any) => {
        const intl = useIntl()
        const placeholder = intl.formatMessage({
            id: 'navbar.search.placeholder.search',
            defaultMessage: 'Search',
        })
        const {
            searchInput,
            searchOutput,
            globalFiltersList,
            onSearch,
            onResetSearch,
            onListItemClick,
            requesting,
        } = props

        const [chips, setChips] = useState<any[]>([])
        const [searchText, setSearchText] = useState<string>('')
        const [results, setResults] = useState<any>()
        const [predictions, setPredictions] = useState<any[]>([])
        const [predictionText, setPredictionText] = useState<string>('')
        const [predictionsLoading, setPredictionsLoading] =
            useState<boolean>(false)
        const [searched, setSearched] = useState<boolean>(false)
        const [timeFrame, setTimeFrame] = useState<{ start: Date; end: Date }>()
        const [debouncedInputValue, setDebouncedInputValue] = useState('')

        useImperativeHandle(ref, () => ({
            resetSectionType: () =>
                createSearchInput(null, null, SectionQuery.both),
        }))

        const handleCompleteChange = (e: AutoCompleteCompleteMethodParams) => {
            setDebouncedInputValue(e.query)
        }

        useEffect(() => {
            let globalFilter = (globalFiltersList || []).find(
                (gf) => gf.isApply
            )
            globalFilter =
                globalFilter ||
                (globalFiltersList || []).find((gf) => gf.isOriginalFilter)
            console.log(globalFilter)
            let end = new Date()
            let start = new Date()
            start.setDate(start.getDate() - 90)
            if (globalFilter?.categories?.timeFrame?.from)
                start = new Date(globalFilter.categories.timeFrame.from)
            if (globalFilter?.categories?.timeFrame?.to)
                end = new Date(globalFilter.categories.timeFrame.to)
            setTimeFrame({ start, end })
        }, [globalFiltersList])

        useEffect(() => {
            const timeoutId = setTimeout(() => {
                onSearchPredictions(debouncedInputValue)
            }, 500)
            return () => clearTimeout(timeoutId)
        }, [debouncedInputValue, 500])

        useEffect(() => {
            if (searchOutput && Object.keys(searchOutput)?.length > 0) {
                const sectionTypes = Object.keys(searchOutput)

                if (!!searchOutput?.sectionFilters?.length) {
                    setChips(searchOutput.sectionFilters)
                } else if (!searchInput.sectionFilter) {
                    setChips(
                        sectionTypes.filter(
                            (key: any) =>
                                Object.values(SectionType).includes(key) &&
                                key !== 'sectionFilters' &&
                                searchOutput[key]?.list?.length > 0
                        )
                    )
                }
                let _results: GlobalSearchOutput = {}
                for (let key of Object.keys(searchOutput)) {
                    if (sectionTypes.includes(key)) {
                        _results[key] = searchOutput[key]
                    }
                }

                setResults(_results)
            } else {
                setChips([])
                setResults({})
            }
            if (searchInput?.search?.length && !searchText?.length) {
                setSearchText(searchInput.search)
                setPredictionText(searchInput.search)
            }
        }, [searchInput, searchOutput])

        useEffect(() => {
            if (requesting) {
                setSearched(true)
            }
        }, [requesting])

        const convertLabel = (label: any) => {
            return intl.formatMessage({
                id: `navbar.search.menu.${label}`,
                defaultMessage: label,
            })
        }

        const createSearchInput = (
            chip?: any,
            searchText?: any,
            reset?: SectionQuery
        ) => {
            const sectionTypes = Object.keys(SectionType)
            const newSearchInput: GlobalSearchInput = { ...searchInput }
            if (searchText) {
                newSearchInput.search = searchText
                newSearchInput.startDate = moment(timeFrame?.start).valueOf()
                newSearchInput.endDate = moment(timeFrame?.end).valueOf()
            }
            if (chip) {
                for (let sectionType of sectionTypes) {
                    if (chip === sectionType) {
                        newSearchInput.sectionType = chip
                        newSearchInput.startDate = moment(
                            timeFrame?.start
                        ).valueOf()
                        newSearchInput.endDate = moment(
                            timeFrame?.end
                        ).valueOf()
                        onSearch(newSearchInput)
                        return
                    }
                }
                newSearchInput.sectionFilter = chip
            }

            if (reset && reset.length) {
                if (reset === SectionQuery.both) {
                    delete newSearchInput.sectionFilter
                    delete newSearchInput.sectionType
                } else {
                    delete newSearchInput[reset]
                }
            }
            // debugger
            onSearch(newSearchInput)
        }

        const onPredictionSelected = (selection: any) => {
            if (selection?.value) {
                createSearchInput(null, selection.value)
            }
        }

        const onSearchPredictions = (searchText: string) => {
            if (predictionText?.length < 3) return
            let input: GlobalSearchInput = {
                search: searchText,
                startDate: moment(timeFrame.start).valueOf(),
                endDate: moment(timeFrame.end).valueOf(),
            }
            setPredictionsLoading(true)
            GlobalSearchService.onSearchPredictions(input)
                .then((response: any) => {
                    setPredictionsLoading(false)
                    if (
                        response?.data?.tips &&
                        Array.isArray(response?.data?.tips)
                    ) {
                        setPredictions(response.data.tips)
                    }
                })
                .catch((error: any) => {
                    setPredictionsLoading(false)
                    // setPredictions(['Project 1', 'Project 2', 'Project 3'])
                })
        }
        const escapeRegExp = (text) =>
            text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
        const boldString = (str: any, substr: any) =>
            str.replace(RegExp(escapeRegExp(substr), 'gi'), (str: string) => {
                return `<span class='bold-found'>${str}</span>`
            })
        const itemTemplate = (item: any) => {
            return (
                <span className="smart-modals-search-prediction">
                    <Markup content={boldString(item, predictionText)} />
                </span>
            )
        }

        const showOneOrAllResults = (item: any[], sectionType: string) => {
            let sectionTypeSearch: boolean = !!searchInput.sectionType
            return (
                item &&
                item.length > 0 &&
                (!sectionTypeSearch || sectionType === searchInput.sectionType)
            )
        }

        const isSectionActive = (item: string) => {
            return (
                item === searchInput.sectionType ||
                item === searchInput.sectionFilter
            )
        }

        const whichSectionActive = (item: string) => {
            return Object.values(SectionType).includes(item as SectionType)
                ? SectionQuery.sectionType
                : Object.values(SectionFilter).includes(item as SectionFilter)
                ? SectionQuery.sectionFilter
                : null
        }

        const onToggleButtonClick = (item: string) => {
            let isActive = isSectionActive(item)
            createSearchInput(
                isActive ? null : item,
                predictionText,
                isActive ? whichSectionActive(item) : null
            )
        }

        const getResultsComponent = () => {
            if (requesting) {
                return (
                    <div className="spinner-wrapper flex">
                        <LuxIconSpinner color={Color.GREY2} />
                    </div>
                )
            }
            let resultsComponent: React.ReactNode = <></>
            if (results) {
                let array: any[] = Object.keys(results)?.filter(
                    (j: any) =>
                        j !== 'sectionFilters' && results[j]?.list?.length > 0
                )

                if (array && array.length) {
                    resultsComponent = array.map((key: any, index: any) => {
                        let item = results[key]
                        let show: boolean = showOneOrAllResults(item.list, key)

                        return show ? (
                            <GlobalSearchList
                                key={index}
                                title={convertLabel(key)}
                                list={item.list}
                                fullHeight={isSectionActive(key)}
                                onViewAll={(e: any) => {
                                    onToggleButtonClick(key)
                                }}
                                onClick={(item: any) =>
                                    onListItemClick(item, key)
                                }
                                totalSize={
                                    item.total_size
                                        ? item.total_size
                                        : item.list.length
                                }
                            />
                        ) : (
                            <></>
                        )
                    })
                } else if (searched) {
                    resultsComponent = (
                        <div className="no-results">
                            <span className="searched-for">{`${intl.formatMessage(
                                {
                                    id: 'navbar.search.menu.searched_for',
                                    defaultMessage: 'You searched for:',
                                }
                            )} ${predictionText}`}</span>
                            <span className="no-results-text">
                                {' '}
                                {intl.formatMessage({
                                    id: 'navbar.search.menu.no_results_text',
                                    defaultMessage:
                                        'We didn’t find any items corresponding exactly to your query',
                                })}
                            </span>
                        </div>
                    )
                }
            }

            return (
                <>
                    <Divider className="results-divider" />
                    <div className="results-lists">{resultsComponent}</div>
                </>
            )
        }

        return (
            <div className="search-container">
                <div className="search-wrapper">
                    <div className="search-bar">
                        <div className="autocomplete-wrapper">
                            <AutoComplete
                                style={{ maxWidth: '100%' }}
                                panelClassName={`smart-modals-global-search-predictions`}
                                placeholder={`${placeholder}...`}
                                value={predictionText}
                                suggestions={predictions}
                                delay={300}
                                minLength={3}
                                min={3}
                                // appendTo={'self'}
                                itemTemplate={itemTemplate}
                                completeMethod={handleCompleteChange}
                                onSelect={(e: any) => {
                                    onPredictionSelected(e)
                                }}
                                onKeyUp={(e: any) => {
                                    if (
                                        e.keyCode === 13 &&
                                        predictionText?.length > 2
                                    ) {
                                        onPredictionSelected({
                                            value: predictionText,
                                        })
                                        const suggestionsPanel: HTMLCollectionOf<Element> =
                                            document.getElementsByClassName(
                                                'smart-modals-global-search-predictions'
                                            )
                                        const element: Element =
                                            suggestionsPanel[0]
                                        element?.classList.add('hide')
                                    }
                                }}
                                width={'100%'}
                                onChange={(e) => {
                                    setPredictionText(e.value)
                                    setSearched(false)
                                    const suggestionsPanel: HTMLCollectionOf<Element> =
                                        document.getElementsByClassName(
                                            'smart-modals-global-search-predictions'
                                        )
                                    const element: Element = suggestionsPanel[0]
                                    element?.classList.remove('hide')
                                }}
                            />
                            {!predictionsLoading ? (
                                predictionText?.length > 0 ? (
                                    <LuxIcon
                                        icon="status-ko"
                                        color={Color.GREY2}
                                        onClick={(e: any) => {
                                            setPredictionText('')
                                            setSearched(false)
                                            onResetSearch()
                                        }}
                                    />
                                ) : (
                                    <LuxIcon
                                        icon="search"
                                        color={Color.GREY1}
                                    />
                                )
                            ) : (
                                <LuxIconSpinner
                                    size={16}
                                    color={Color.GREY2}
                                    classes="autocomplete-spinner"
                                />
                            )}
                        </div>
                    </div>
                    <div className="daterange-wrapper flex align-items-center mt-3">
                        <LuxButton
                            text={intl.formatMessage({
                                defaultMessage: '-3 months',
                                id: 'navbar.globalsearch.minusthree',
                            })}
                            onClick={() => {
                                setTimeFrame({
                                    start: moment(timeFrame.start)
                                        .subtract(90, 'days')
                                        .toDate(),
                                    end: moment(timeFrame.end)
                                        .subtract(90, 'days')
                                        .toDate(),
                                })
                            }}
                            variant="outlined"
                            color={Color.BLUE}
                        />
                        <LuxDateRangePicker
                            value={timeFrame}
                            onChangeCallback={(val) => {
                                setTimeFrame(val)
                            }}
                            singleInput
                            maxDaysRange={90}
                        />
                        <LuxButton
                            text={intl.formatMessage({
                                defaultMessage: '+3 months',
                                id: 'navbar.globalsearch.plusthree',
                            })}
                            onClick={() => {
                                setTimeFrame({
                                    start: moment(timeFrame.start)
                                        .add(90, 'days')
                                        .toDate(),
                                    end: moment(timeFrame.end)
                                        .add(90, 'days')
                                        .toDate(),
                                })
                            }}
                            variant="outlined"
                            color={Color.BLUE}
                        />
                    </div>
                    <div className="results-content flex">
                        <div className="text-mini flex align-items-center mb-3">
                            <LuxIcon
                                icon="info"
                                size={16}
                                color={Color.GREY1}
                                classes="mr-2"
                            />
                            {intl.formatMessage({
                                defaultMessage:
                                    'The resources section is not affected by the 3-month date filter above.',
                                id: 'navbar.globalsearch.disclaimer.threemonths.resources',
                            })}
                        </div>
                    </div>
                    {searched && (
                        <div className="results-content flex">
                            <div className="text-mini flex align-items-center mb-3">
                                <LuxIcon
                                    icon="info"
                                    size={16}
                                    color={Color.GREY1}
                                    classes="mr-2"
                                />
                                {intl.formatMessage({
                                    defaultMessage:
                                        'The below results refer to a period of up to 3 months, change the time range on the above filters to check for further results.',
                                    id: 'navbar.globalsearch.disclaimer.threemonths',
                                })}
                            </div>
                            <div className="chips-bar flex">
                                {chips &&
                                    [...chips]?.map(
                                        (item: any, index: number) => (
                                            <LuxToggleButton
                                                key={index}
                                                label={convertLabel(item)}
                                                active={isSectionActive(item)}
                                                componentClass={'chip flex'}
                                                onButtonChange={(e: any) => {
                                                    !requesting
                                                        ? onToggleButtonClick(
                                                              item
                                                          )
                                                        : () => {}
                                                }}
                                            />
                                        )
                                    )}
                            </div>
                            {getResultsComponent()}
                        </div>
                    )}
                </div>
            </div>
        )
    }
)
